import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@apex/shared";

export interface FilterChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Whether the chip represents an active filter (renders in primary color). */
  active?: boolean;
  /** Optional small dismiss button rendered to the right of the label. */
  onDismiss?: () => void;
}

/**
 * FilterChip — pill-shaped button used in the FilterBar to open a filter
 * popover. Active chips are styled in the tenant primary color; inactive
 * chips show a subtle outline. The chip is a real `<button>` so it
 * participates in tab order and keyboard activation.
 */
export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(
  ({ className, active, onDismiss, children, ...props }, ref) => {
    return (
      <span className="apex-relative apex-inline-flex apex-items-center">
        <button
          ref={ref}
          type="button"
          aria-pressed={active}
          className={cn(
            "apex-inline-flex apex-items-center apex-gap-1.5 apex-rounded-full apex-px-3 apex-py-1.5",
            "apex-text-xs apex-font-medium apex-transition-colors apex-min-h-[32px]",
            "focus-visible:apex-outline-none focus-visible:apex-ring-2 focus-visible:apex-ring-ring focus-visible:apex-ring-offset-1",
            active
              ? "apex-bg-primary/10 apex-text-primary apex-border apex-border-primary/40 hover:apex-bg-primary/15"
              : "apex-bg-background apex-text-foreground apex-border apex-border-border hover:apex-bg-muted",
            className
          )}
          {...props}
        >
          {children}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="apex-shrink-0 apex-opacity-70"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {active && onDismiss && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            className={cn(
              "apex-ml-1 apex-rounded-full apex-p-0.5 apex-text-muted-foreground",
              "hover:apex-bg-muted hover:apex-text-foreground apex-transition-colors",
              "focus-visible:apex-outline-none focus-visible:apex-ring-2 focus-visible:apex-ring-ring"
            )}
            aria-label="Clear this filter"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        )}
      </span>
    );
  }
);

FilterChip.displayName = "FilterChip";
